import type {
  DragUpdateOptions,
  DragUpdateResult,
} from "@project/emerge-types";
import type * as THREE from "three";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import type { RackInteriorOptions } from "../../interfaces/RackInteriorOptions";
import {
  dimensionFromCornerDelta,
  gridStepFromOptions,
} from "../../utils/dragGrid";
import {
  buildBoxCornerConstraint,
  cornerAxisPosition,
  cornerAxisSign,
} from "./cornerGeometry";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

const MIN_SIZE = 50;
const MAX_SIZE = 5000;

/**
 * Rescale tray sizes by `target / live`, so they keep their proportions as the
 * dimension they divide up changes.
 *
 * Both ends are absolute rather than incremental, so re-running this for the
 * frames that share one drag context converges on the same arrangement instead
 * of scaling repeatedly. An unchanged dimension returns the input untouched:
 * rounding is lossy, and a drag released where it started must not nudge sizes
 * the user entered off-grid.
 *
 * Sizes round to whole millimetres rather than to the drag grid — rounding each
 * entry to 10 mm would let the achieved total drift several millimetres from
 * the target, which shows up as the case not following the pointer.
 */
function scaleToTarget(values: number[], live: number, target: number) {
  if (live <= 0 || target === live) return null;
  const scale = target / live;
  return values.map((value) => Math.max(MIN_SIZE, Math.round(value * scale)));
}

/**
 * Drag handler for RACK interior type.
 * Always enabled - resizes overall dimensions with proportional scaling.
 */
export const RackCornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings): boolean {
    return settings.interiorOptions.type === InteriorType.RACK;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const rack = settings.interiorOptions as RackInteriorOptions;

    return buildBoxCornerConstraint(
      caseCornerPosition,
      position,
      "rack",
      {
        currentWidth:
          typeof rack.width === "number" ? rack.width : innerDimensions.x,
        currentHeight: rack.heights?.reduce((sum, h) => sum + h, 0) ?? 0,
        currentDepth: rack.depth ?? innerDimensions.z,
      },
      MIN_SIZE,
      MAX_SIZE,
    );
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "xz" | "y";
    const interior = currentSettings.interiorOptions as RackInteriorOptions;
    const gridStepMm = gridStepFromOptions(options);

    if (mode === "xz") {
      const restX = settingsContext.restX as number | undefined;
      const restZ = settingsContext.restZ as number | undefined;
      const currentWidth = settingsContext.currentWidth as number | undefined;
      const currentDepth = settingsContext.currentDepth as number | undefined;
      if (
        restX === undefined ||
        restZ === undefined ||
        currentWidth === undefined ||
        currentDepth === undefined
      ) {
        return null;
      }

      const xSign = cornerAxisSign(restX);
      const zSign = cornerAxisSign(restZ);
      const newWidth = dimensionFromCornerDelta(
        currentWidth,
        xSign * (newPosition.x - restX),
        MIN_SIZE,
        gridStepMm,
      );
      const newDepth = dimensionFromCornerDelta(
        currentDepth,
        zSign * (newPosition.z - restZ),
        MIN_SIZE,
        gridStepMm,
      );

      const actualCurrentWidth =
        typeof interior.width === "number" ? interior.width : 465;
      const scaledWidths = interior.widths.map(
        (row) => scaleToTarget(row, actualCurrentWidth, newWidth) ?? row,
      );

      const snappedPosition = newPosition.clone();
      snappedPosition.x = cornerAxisPosition(
        restX,
        xSign,
        currentWidth,
        newWidth,
      );
      snappedPosition.z = cornerAxisPosition(
        restZ,
        zSign,
        currentDepth,
        newDepth,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            width: newWidth,
            depth: newDepth,
            widths: scaledWidths,
          },
        },
        displayValues: {
          primary: {
            label: "W×D",
            value: newWidth,
            unit: `×${newDepth}mm`,
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    } else {
      const restY = settingsContext.restY as number | undefined;
      const currentHeight = settingsContext.currentHeight as number | undefined;
      if (restY === undefined || currentHeight === undefined) return null;

      const targetHeight = dimensionFromCornerDelta(
        currentHeight,
        newPosition.y - restY,
        MIN_SIZE,
        gridStepMm,
      );

      const actualCurrentHeight = interior.heights.reduce(
        (sum, h) => sum + h,
        0,
      );
      const scaledHeights =
        scaleToTarget(interior.heights, actualCurrentHeight, targetHeight) ??
        interior.heights;

      // Per-tray rounding can land a millimetre or two off the target, so the
      // readout and the snapped corner follow what was actually applied.
      const achievedHeight = scaledHeights.reduce((sum, h) => sum + h, 0);
      const snappedPosition = newPosition.clone();
      snappedPosition.y = cornerAxisPosition(
        restY,
        1,
        currentHeight,
        achievedHeight,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            heights: scaledHeights,
          },
        },
        displayValues: {
          primary: {
            label: "H",
            value: achievedHeight,
            unit: "mm",
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    }
  },
};
